import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND } from 'lexical';
import { Icon } from '@iconify/react';
import { Box, IconButton, Stack } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import { LowPriority } from '@mui/icons-material';
import { INSERT_IMAGE_COMMAND } from '../commands';

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  const updateToolbar = useCallback(() => {
    // if the currently selected text has bold or italic formatting, update the state on the editor toolbar
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
    }
  }, []);

  useEffect(() => {
    // this is registers the SELECTION_CHANGE_COMMAND with the editor
    // when this command is executed, the updateToolbar function is called
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      1
    );
  }, [editor, updateToolbar]);

  const formatText = (format: 'bold' | 'italic') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    // Update the state immediately for better UI feedback
    switch (format) {
      case 'bold':
        setIsBold(!isBold);
        break;
      case 'italic':
        setIsItalic(!isItalic);
        break;
    }
  };

  const insertMedia = () => {
    // Create a hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e: Event) => {
      const files = Array.from((e?.target as HTMLInputElement).files || []);
      // dispatches the INSERT_IMAGE_COMMAND with the files
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, files);
    };
    input.click();
  };

  return (
    <Box sx={{ 
      borderBottom: '1px solid #313131',
      p: 0.5
    }}>
      <Stack direction="row" spacing={1}>
        <IconButton 
          size="small" 
          onClick={() => formatText('bold')}
          sx={{ 
            color: 'white',
            backgroundColor: isBold ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: isBold 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          <Icon icon="mdi:format-bold" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => formatText('italic')}
          sx={{ 
            color: 'white',
            backgroundColor: isItalic ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: isItalic 
                ? 'rgba(255, 255, 255, 0.2)' 
                : 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          <Icon icon="mdi:format-italic" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={insertMedia}
          sx={{ color: 'white' }}
        >
          <Icon icon="mdi:image" />
        </IconButton>
      </Stack>
    </Box>
  );
} 